import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { i18next, dialogs } from '@yola/ws-sdk';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import segment from 'src/js/modules/analytics/segment';
import upsell from 'src/js/modules/upsell';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import useMounted from 'src/js/modules/utils/custom-hooks/use-mounted';
import textDecorationAttributes from '../constants/text-decoration-attributes';
import useTextDecorations from '../hooks/use-text-decorations';
import TextDecorationLibraryDialog from '../components/text-decoration-library-dialog';

const {
  hostingPackageFeatureNames: { PREMIUM_TEXT_DECORATIONS },
} = subscriptionManagerConstants;
const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const getCaptions = () => ({
  title: i18next.t('Text effects library'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
});

const TextDecorationLibraryContainer = ({
  initialDecorationType,
  initialDialogPosition,
  elements,
  premiumAvailable,
  getSharedData,
  resolveSharedData,
}) => {
  const captions = getCaptions();

  const { decorations, getDecorationById } = useTextDecorations();
  const [dialogPosition, setPosition] = useState(initialDialogPosition);
  const [decorationType, setDecorationType] = useState(elements[0].type);
  const [isUpgradeFlow, setIsUpgradeFlow] = useState(false);
  const activeDecorationRef = useRef(null);
  const isMounted = useMounted();

  const saveSharedData = () => {
    resolveSharedData({
      ...getSharedData(),
      initialDialogPosition: dialogPosition,
    });
  };

  const setDecoration = (type) => {
    setDecorationType(type);

    elements.forEach((element) => {
      element.setAttribute(textDecorationAttributes.TYPE, type);
    });
  };

  const handleDecorationChange = (id, isPremium) => {
    trackEvent(events.TEXT_DECORATION_PRESET_CLICKED, {
      premiumAvailable,
      textDecorationPresetSelected: id,
      premiumPreset: Boolean(isPremium),
    });

    if (isPremium && id !== initialDecorationType) {
      setIsUpgradeFlow(true);

      const handleUpgradeResolve = () => {
        setDecoration(id);
        setIsUpgradeFlow(false);
      };

      const handleUpgradeReject = () => {
        if (!isMounted.current) {
          dialogs.operations.show(dialogTypes.TEXT_DECORATION_LIBRARY_DIALOG, {
            ...props,
            initialDialogPosition: dialogPosition,
          });

          return;
        }

        setIsUpgradeFlow(false);
      };

      upsell.operations
        .requestFeatureAvailability(PREMIUM_TEXT_DECORATIONS)
        .then(handleUpgradeResolve, handleUpgradeReject);
    } else {
      saveSharedData();
      setDecoration(id);
    }
  };

  const handleSumbit = () => {
    saveSharedData();
    trackEvent(events.TEXT_DECORATION_PRESET_SELECTED, {
      premiumAvailable,
      textDecorationPresetSelected: decorationType,
      premiumPreset: Boolean(getDecorationById(decorationType).premium),
    });
    dialogs.operations.show(dialogTypes.TEXT_DECORATION_SETTINGS_DIALOG, {
      elements,
      premiumAvailable,
    });
  };

  const handleCancel = () => {
    saveSharedData();
    setDecoration(initialDecorationType);
    dialogs.operations.show(dialogTypes.TEXT_DECORATION_SETTINGS_DIALOG, {
      elements,
      premiumAvailable,
    });
  };

  const handleDialogReady = () => {
    activeDecorationRef.current?.scrollIntoView();
  };

  const handleOverlayClick = () => {
    if (isUpgradeFlow) {
      return;
    }

    handleSumbit();
  };

  const handleDialogDragEnd = (event, position) => {
    setPosition(position);
  };

  return (
    <TextDecorationLibraryDialog
      decorations={decorations}
      activeDecorationRef={activeDecorationRef}
      activeDecorationId={decorationType}
      initialDecorationId={initialDecorationType}
      dialogPosition={initialDialogPosition}
      captions={captions}
      premiumAvailable={premiumAvailable}
      onDecorationChange={handleDecorationChange}
      onSubmit={handleSumbit}
      onCancel={handleCancel}
      onDialogReady={handleDialogReady}
      onOverlayClick={handleOverlayClick}
      onDialogDragEnd={handleDialogDragEnd}
    />
  );
};

TextDecorationLibraryContainer.propTypes = {
  initialDecorationType: PropTypes.string.isRequired,
  initialDialogPosition: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  elements: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape(), PropTypes.node])).isRequired,
  premiumAvailable: PropTypes.bool.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
};

export default TextDecorationLibraryContainer;
